/**
 * LooseRelationshipPeek — live counts + 5-row target sample inside the loose-FK editor.
 * Empirical confirmation that the filter does what the user intended.
 */

import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { useLooseRelationshipPeek } from '../../hooks/useLooseRelationshipPeek';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { FilterGroup } from '../../../core/types/workspaceTypes';

export type LooseRelationshipPeekProps = {
  connectionId: string;
  database: string;
  source: { schema: string; table: string; filter?: FilterGroup[] };
  target: {
    schema: string;
    table: string;
    keyColumn: string;
    displayColumn?: string;
    filter?: FilterGroup[];
  };
};

export function LooseRelationshipPeek(props: LooseRelationshipPeekProps) {
  const { source, target, refetch } = useLooseRelationshipPeek({ ...props, enabled: true });
  const { track } = useAnalytics();

  const onRefresh = () => {
    track('feature:loose_fk_peek_refresh');
    refetch();
  };

  const targetHasFilter = !!props.target.filter && props.target.filter.length > 0;

  return (
    <div className="flex flex-col gap-1 text-xs" data-testid="loose-fk-peek">
      <div className="flex items-center justify-between">
        <span className="font-medium">Peek</span>
        <Button size="sm" variant="ghost" onClick={onRefresh} data-testid="loose-fk-peek-refresh">
          <RefreshCw className="h-3 w-3" />
        </Button>
      </div>

      {source.enabled && (
        <div data-testid="loose-fk-peek-source-count">
          Source:{' '}
          {source.isLoading ? (
            <RandomDotmSpinner
              size={12}
              className="inline-block align-middle text-muted-foreground"
            />
          ) : (
            <span className={source.count === 0 ? 'text-destructive' : ''}>
              {source.count} rows match
            </span>
          )}
        </div>
      )}

      <div data-testid="loose-fk-peek-target-count">
        Target:{' '}
        {target.isLoading ? (
          <RandomDotmSpinner
            size={12}
            className="inline-block align-middle text-muted-foreground"
          />
        ) : (
          <span className={target.count === 0 ? 'text-destructive' : ''}>
            {target.count} rows {targetHasFilter ? 'match' : 'total'}
          </span>
        )}
      </div>

      {target.error && (
        <div className="text-destructive">
          Peek failed:{' '}
          {target.error instanceof Error ? target.error.message : String(target.error)}
        </div>
      )}

      {!target.isLoading && target.count === 0 ? (
        <div className="italic text-muted-foreground border rounded p-2">No matches</div>
      ) : (
        <div
          className="overflow-auto border rounded"
          style={{ maxHeight: 320 }}
          data-testid="loose-fk-peek-sample"
        >
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-muted/80">
              <tr>
                {target.columns.map((c) => (
                  <th
                    key={c.name}
                    className="text-left px-2 py-1 font-medium text-muted-foreground border-b border-border whitespace-nowrap"
                    translate="no"
                  >
                    {c.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {target.rows.slice(0, 5).map((row, i) => (
                <tr key={i} className="hover:bg-muted/40">
                  {target.columns.map((c) => {
                    const v = row[c.name];
                    const display =
                      v === null || v === undefined
                        ? ''
                        : typeof v === 'object'
                          ? JSON.stringify(v)
                          : String(v);
                    return (
                      <td
                        key={c.name}
                        className="px-2 py-1 border-b border-border/40 whitespace-nowrap overflow-hidden text-ellipsis max-w-[240px]"
                        translate="no"
                        title={display}
                      >
                        {display === '' ? (
                          <span className="text-muted-foreground italic">NULL</span>
                        ) : (
                          display
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
